import React, { Fragment, useCallback, useEffect, useState } from "react";
import "./AddStock.scss";
import AsyncSelect from "react-select/async";
import { Form, Button, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import { Card, CardBody, Col, Input } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { addStock } from "./StockService";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { suppliersMasterAPI } from "../PharmacySuppliers/SupplierService";
import { FaArrowLeft } from "react-icons/fa6";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";
import { useCurrency } from "../../_helper/CurrencyContext";
import { PlusCircle } from "react-feather";
import SupplierModal from "../PharmacySuppliers/SupplierModal";
import InfoIcon from "./components/InfoIcon";
import { COLUMN_INFO, PURCHASE_ITEM_SEARCH } from "./stoctTableInfo";
import { usePurchaseMedicineResearchSearch } from "./hooks/usePurchaseMedicineResearchSearch";
import { usePurchaseInventorySearch } from "./hooks/usePurchaseInventorySearch";
import { addNewItem } from "../PharmacyMedicineAdd/PharmacyMedicineService";
import {
    buildResearchAddPayload,
    buildPurchaseRowFromResearch,
    hasMissingResearchFields,
} from "./utils/researchQuickAdd";
import ResearchItemDetailsModal from "./components/ResearchItemDetailsModal";
import { generateInternalEan13 } from "../../utils/internalEan13";
import {
    getActivePosTaxType,
    isPharmacyTaxEnabled,
    shouldApplyTaxToStockItem,
} from "../PharmacyManageTax/taxConfigUtils";

const getMasterMedicineQuantity = (medicine) => {
    const qty = medicine?.package_qty ?? medicine?.quantity ?? medicine?.packageQuantity;
    if (qty === null || qty === undefined || qty === "") return null;
    const num = Number(qty);
    return Number.isFinite(num) ? num : null;
};

const getMasterTypeLabel = (medicine, isResearchOTC, t) => {
    if (medicine?.medicine_type === "otc" || isResearchOTC) return t("OTC");
    if (medicine?.medicine_type === "unani" || medicine?.isUnani || medicine?.type === 'unani') return t("U Medicine");
    return t("Medicine");
};

const getMasterTypeBadgeClass = (medicine, isResearchOTC) => {
    if (medicine?.medicine_type === "otc" || isResearchOTC) return "otc";
    if (medicine?.medicine_type === "unani" || medicine?.isUnani || medicine?.type === 'unani') return "unani";
    return "med";
};

const getInventoryTypeLabel = (med, t) => {
    if (med?.isOTC) return t("OTC");
    if (med?.isUnani || med?.type === 'unani') return t("U Medicine");
    return t("Medicine");
};

const getInventoryTypeBadgeClass = (med) => {
    if (med?.isOTC) return "otc";
    if (med?.isUnani || med?.type === 'unani') return "unani";
    return "med";
};

const getInventoryComposition = (med) => {
    if (med?.isOTC) return "";
    const salt = med?.saltName?.trim() || "";
    const brand = med?.brandName?.trim() || "";
    if (!salt || salt === brand) return "";
    return salt;
};

const getMasterComposition = (medicine, isResearchOTC) => {
    if (medicine?.medicine_type === "otc" || isResearchOTC) return "";
    const composition =
        medicine?.salt_composition?.trim() ||
        medicine?.salt_composition_new?.trim() ||
        "";
    if (!composition || composition === "—") return "";
    const name = medicine?.name?.trim() || "";
    if (name && composition === name) return "";
    return composition;
};

const PurchaseSearchResultRow = ({
    t,
    productForm,
    manufacturer,
    name,
    packageLabel,
    quantity,
    composition,
    typeLabel,
    typeClass,
    onAdd,
    adding = false,
    selectVariant = false,
}) => (
    <div className="purchase-db-row">
        <div className="purchase-db-row-info">
            <div className="purchase-db-row-top">
                {productForm ? (
                    <span className="purchase-meta-badge purchase-meta-badge--form">
                        {productForm}
                    </span>
                ) : null}
                <span className="purchase-db-name" title={name}>
                    {name}
                </span>
                {packageLabel ? (
                    <span className="purchase-meta-badge purchase-meta-badge--package">
                        {packageLabel}
                    </span>
                ) : null}
                {quantity != null ? (
                    <span className="purchase-meta-badge purchase-meta-badge--qty">
                        {t("Qty")}: {quantity}
                    </span>
                ) : null}
                <span className={`purchase-research-badge ms-auto ${typeClass}`}>{typeLabel}</span>
            </div>
            {composition ? (
                <div className="purchase-db-composition" title={composition}>
                    {composition}
                </div>
            ) : null}
            {manufacturer ? (
                <span className="purchase-meta-badge purchase-meta-badge--form">
                    {manufacturer}
                </span>
                // <span className={`purchase-research-badge mt-1 ${typeClass === "med" ? "otc" : "med"}`}
                //     style={{ cursor: "help" }}>{t("Manufacturer")}
                //     <InfoIcon text={manufacturer} icon="question" />
                // </span>
            ) : null}
        </div>
        <div className="purchase-db-row-actions">
            <button
                type="button"
                className={`btn btn-sm purchase-research-add-btn${selectVariant ? " purchase-research-add-btn--select" : ""}`}
                onClick={onAdd}
                disabled={adding}
            >
                <PlusCircle size={13} />
                <span>{adding ? t("Adding...") : t("Add")}</span>
            </button>
        </div>
    </div>
);

const getPurchaseSelectTheme = (isDark) => (theme) => ({
    ...theme,
    colors: {
        ...theme.colors,
        primary: isDark ? "#6b7fd7" : "#1b255b",
        primary25: isDark ? "#3a3f4f" : "#e8f4ff",
        primary50: isDark ? "#4a5464" : "#d6e8ff",
        neutral0: isDark ? "#262932" : "#ffffff",
        neutral5: isDark ? "#2b2e38" : "#f8fafc",
        neutral10: isDark ? "#323846" : "#f1f5f9",
        neutral20: isDark ? "#4a5464" : "#e2e8f0",
        neutral30: isDark ? "#5c6678" : "#cbd5e1",
        neutral40: isDark ? "#8b95a8" : "#94a3b8",
        neutral50: isDark ? "#a8b0be" : "#64748b",
        neutral60: isDark ? "#c5cbd6" : "#475569",
        neutral70: isDark ? "#dce1e9" : "#334155",
        neutral80: isDark ? "#e8eaef" : "#1e293b",
        neutral90: isDark ? "#f3f4f6" : "#0f172a",
    },
});

const getPurchaseSelectStyles = (isDark) => ({
    menuPortal: (base) => ({ ...base, zIndex: 1060 }),
    menu: (base) => ({
        ...base,
        fontSize: "13px",
        boxShadow: isDark
            ? "0 10px 28px rgba(0, 0, 0, 0.45)"
            : "0 10px 28px rgba(15, 23, 42, 0.14)",
    }),
    option: (base, state) => ({
        ...base,
        fontSize: "13px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.4,
        padding: "8px 12px",
    }),
    control: (base, state) => ({
        ...base,
        minHeight: "38px",
        backgroundColor: isDark ? "#262932" : base.backgroundColor,
        borderColor: state.isFocused
            ? isDark ? "#6b7fd7" : "#1b255b"
            : isDark ? "#4a5464" : base.borderColor,
        boxShadow: "none",
        "&:hover": {
            borderColor: isDark ? "#6b7fd7" : "#1b255b",
        },
    }),
    input: (base) => ({
        ...base,
        color: isDark ? "#e8eaef" : base.color,
    }),
    singleValue: (base) => ({
        ...base,
        color: isDark ? "#e8eaef" : base.color,
    }),
    placeholder: (base) => ({
        ...base,
        color: isDark ? "#8b95a8" : "#94a3b8",
    }),
});

const AddStock = () => {
    const { t } = useTranslation();
    const { getCurrencySymbol } = useCurrency();
    const { loading, setLoading } = useLoader();
    const { pharmacyPOS } = usePharmacy();
    const navigate = useNavigate();
    const [currency] = useState("INR");

    const [supplier, setSupplier] = useState(null);
    const [recentSuppliers, setRecentSuppliers] = useState([]);
    const [searchQuerySupplier, setSearchQuerySupplier] = useState('');

    const [items, setItems] = useState([]);
    const [totalPaid, setTotalPaid] = useState("");
    const [note, setNote] = useState("");
    const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
    const [errors, setErrors] = useState({});
    const [isTotalPaidModified, setIsTotalPaidModified] = useState(false);

    const disableScrollProps = useDisableScrollOnFocus();

    // Tax system
    const [pharmacyStateCode] = useState(pharmacyPOS?.stateCode || "");
    const [supplierStateCode, setSupplierStateCode] = useState("");
    const posTaxType = getActivePosTaxType(pharmacyPOS, "gst");
    const showPurchaseTax = isPharmacyTaxEnabled(pharmacyPOS);
    const [isSameState, setIsSameState] = useState(true);

    // Totals
    const [netTotal, setNetTotal] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [taxSummary, setTaxSummary] = useState({
        CGST: 0,
        SGST: 0,
        IGST: 0,
        VAT: 0,
        totalTax: 0,
    });

    const [addSupplierTooltipOpen, setAddSupplierTooltipOpen] = useState(false);
    const toggleAddSupplierTooltip = () => setAddSupplierTooltipOpen(!addSupplierTooltipOpen);
    const [addItemTooltipOpen, setAddItemTooltipOpen] = useState(false);
    const toggleAddItemTooltip = () => setAddItemTooltipOpen(!addItemTooltipOpen);

    const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
    const toggleAddSupplierModal = () => setShowAddSupplierModal((s) => !s);

    // Persists after AsyncSelect blur — react-select clears input on menu-close/input-blur
    const [researchSearchQuery, setResearchSearchQuery] = useState('');
    const [itemSearchKey, setItemSearchKey] = useState(0);
    const [selectedMedicine, setSelectedMedicine] = useState(null);

    const resetResearchSearchState = useCallback(() => {
        setResearchSearchQuery('');
        setSelectedMedicine(null);
        setItemSearchKey((k) => k + 1);
    }, []);

    const handleMedicineSearchChange = (newValue, actionMeta) => {
        const action = actionMeta?.action;
        if (action === 'input-change') {
            setResearchSearchQuery(newValue);
            return newValue;
        }
        return researchSearchQuery;
    };

    // ── Research-only medicine search (isolated, no effect on existing flow) ──
    const [researchType, setResearchType] = useState("medicine");
    const isResearchOTC = researchType === "otc";
    const {
        results: inventoryResults,
        loading: inventoryLoading,
    } = usePurchaseInventorySearch(
        researchSearchQuery,
        pharmacyPOS?.hospital_id,
        isResearchOTC
    );
    const {
        results: researchMedicines,
        loading: researchLoading,
        isActive: isResearchActive,
    } = usePurchaseMedicineResearchSearch(researchSearchQuery, isResearchOTC);

    const [addingResearchId, setAddingResearchId] = useState(null);
    const [researchDetailsOpen, setResearchDetailsOpen] = useState(false);
    const [pendingResearchMedicine, setPendingResearchMedicine] = useState(null);

    const addResearchItemToPurchase = useCallback(
        (stockRow) => {
            if (!stockRow?._id) return;
            if (items.find((i) => i._id === stockRow._id)) {
                toast.info(t("Item is already in the purchase list"));
                return;
            }
            const row = shouldApplyTaxToStockItem(pharmacyPOS, stockRow)
                ? stockRow
                : { ...stockRow, taxPercentage: 0 };
            setItems((prev) => [...prev, row]);
        },
        [items, pharmacyPOS, t]
    );

    const executeResearchQuickAdd = useCallback(
        async (medicine, fieldOverrides = {}) => {
            if (!medicine?._id || addingResearchId) return;

            const payload = buildResearchAddPayload(
                medicine,
                isResearchOTC,
                pharmacyPOS?.hospital_id,
                pharmacyPOS?.baseCurrency,
                fieldOverrides
            );

            try {
                setAddingResearchId(medicine._id);
                const res = await addNewItem(payload);
                const created = res.data?.data || {
                    ...payload,
                    _id: res.data?.item?._id || res.data?.itemId,
                };
                const stockRow = buildPurchaseRowFromResearch(created, medicine, isResearchOTC);
                addResearchItemToPurchase(stockRow);
                toast.success(res.data?.message || t("Item added to purchase"));
                resetResearchSearchState();
            } catch (error) {
                console.error("Quick add from research failed:", error);
            } finally {
                setAddingResearchId(null);
            }
        },
        [
            addingResearchId,
            isResearchOTC,
            pharmacyPOS?.hospital_id,
            pharmacyPOS?.baseCurrency,
            addResearchItemToPurchase,
            resetResearchSearchState,
            t,
        ]
    );

    const handleQuickAddFromResearch = useCallback(
        (medicine) => {
            if (!medicine?._id || addingResearchId) return;

            if (hasMissingResearchFields(medicine)) {
                setPendingResearchMedicine(medicine);
                setResearchDetailsOpen(true);
                return;
            }

            executeResearchQuickAdd(medicine);
        },
        [addingResearchId, executeResearchQuickAdd]
    );

    const handleResearchDetailsClose = () => {
        if (addingResearchId) return;
        setResearchDetailsOpen(false);
        setPendingResearchMedicine(null);
    };

    const handleResearchDetailsConfirm = async (fieldOverrides) => {
        if (!pendingResearchMedicine) return;
        await executeResearchQuickAdd(pendingResearchMedicine, fieldOverrides);
        setResearchDetailsOpen(false);
        setPendingResearchMedicine(null);
    };

    const handleResearchTypeChange = (type) => {
        setResearchType(type);
        resetResearchSearchState();
    };

    // Load Suppliers
    const loadSuppliers = async (inputValue) => {
        // if (!inputValue) return [];
        try {
            setLoading(true);
            const res = await suppliersMasterAPI({ search: inputValue, clinicId: pharmacyPOS?.hospital_id });
            const suppliers = (res.data.data || []).map(s => ({
                value: s._id,
                label: `${s.companyName} [${s.contactPerson || 'No Contact'}]`,
                raw: s
            }));
            setRecentSuppliers(suppliers.slice(0, 5));
            return suppliers;
        } catch (err) {
            return [];
        } finally {
            setLoading(false);
        }
    };
    // AsyncSelect loader — inventory panel is driven by usePurchaseInventorySearch
    const loadMedicines = async (inputValue) => {
        if (!inputValue) return [];
        return inventoryResults.map((m) => ({
            value: m._id,
            label: `${m.saltName} ${!m.isOTC ? `[${m.brandName}]` : ""}`,
            raw: m,
        }));
    };

    // Select Supplier
    const handleSelectSupplier = (option) => {
        const sup = option ? option.raw : null;
        setSupplier(sup);
        setSupplierStateCode(sup?.stateCode || "");

        // Calculate if same state after setting supplier
        if (sup) {
            const newIsSameState = determineTaxType(pharmacyStateCode, sup.stateCode);
            setIsSameState(newIsSameState);
        } else {
            setIsSameState(true); // No supplier = use SGST/CGST as default
        }
    };

    // Helper function to determine tax type
    const determineTaxType = (pharmacyCode, supplierCode) => {
        // If either is missing or empty, use SGST/CGST (intra-state)
        if (!pharmacyCode || pharmacyCode.trim() === "" || !supplierCode || supplierCode.trim() === "") {
            return true; // Use SGST/CGST
        }

        // If both are available, compare them
        return pharmacyCode === supplierCode;
    };

    // Add Item
    const handleSelectMedicine = (option) => {
        if (!option) {
            resetResearchSearchState();
            return;
        }
        const med = option.raw;
        if (items.find(i => i._id === med._id)) {
            toast.info(t("Item is already in the purchase list"));
            resetResearchSearchState();
            return;
        }

        setItems(prev => [...prev, {
            ...med,
            barcode: (med.barcode && String(med.barcode).trim()) || generateInternalEan13(pharmacyPOS?.hospital_id),
            stkQuantity: 1,
            itemQuantity: med.packageQuantity || 1,
            unitPrice: med.regularPrice || 0,
            manufacturer: med.manufacturer,
            unitSalePrice: 0,
            batchNo: "",
            manufacturingDate: "",
            expiryDate: "",
            discount: 0,
            taxPercentage: shouldApplyTaxToStockItem(pharmacyPOS, med) ? (med.tax || 0) : 0,
            taxAmount: 0,
            CGSTPercent: 0, CGSTAmount: 0,
            SGSTPercent: 0, SGSTAmount: 0,
            IGSTPercent: 0, IGSTAmount: 0,
            VATPercent: 0, VATAmount: 0,
        }]);
        resetResearchSearchState();
    };

    const handleSelectInventoryItem = (med) => {
        handleSelectMedicine({
            value: med._id,
            label: `${med.saltName} ${!med.isOTC ? `[${med.brandName}]` : ""}`,
            raw: med,
        });
    };

    // Generic Updater
    const updateItem = (_id, field, value) => {
        setItems(prev => prev.map(item =>
            item._id === _id ? { ...item, [field]: value } : item
        ));
    };

    // Special: Update Package Quantity → auto update itemQuantity
    const updatePackageQty = (_id, value) => {
        const num = Math.max(1, Number(value) || 1);
        setItems(prev => prev.map(item =>
            item._id === _id
                ? { ...item, stkQuantity: num, itemQuantity: num * (item.packageQuantity || 1) }
                : item
        ));
    };

    // Remove Item
    const removeItem = (_id) => setItems(prev => prev.filter(i => i._id !== _id));

    // Recalculate Everything
    const recalculateAll = useCallback(() => {
        if (items.length === 0) {
            setNetTotal(0);
            setTotalPrice(0);
            setTaxSummary({ CGST: 0, SGST: 0, IGST: 0, VAT: 0, totalTax: 0 });
            if (!isTotalPaidModified) setTotalPaid("");
            return;
        }

        let subtotal = 0;
        let totalCGST = 0, totalSGST = 0, totalIGST = 0, totalVAT = 0;

        // Determine tax type based on state codes
        const useIntraState = determineTaxType(pharmacyStateCode, supplierStateCode);
        setIsSameState(useIntraState);

        const updated = items.map(item => {
            const pkgQty = Number(item.stkQuantity) || 0;
            const price = Number(item.unitPrice) || 0;
            const discount = Number(item.discount) || 0;

            const base = pkgQty * price;
            const taxable = Math.max(0, base - discount);
            subtotal += taxable;

            let cgstP = 0, sgstP = 0, igstP = 0, vatP = 0;
            let cgstA = 0, sgstA = 0, igstA = 0, vatA = 0;

            const taxP = shouldApplyTaxToStockItem(pharmacyPOS, item)
                ? Number(item.taxPercentage) || 0
                : 0;

            if (showPurchaseTax && posTaxType === "gst") {
                if (useIntraState) {
                    // Intra-state: Use SGST/CGST
                    cgstP = taxP / 2;
                    sgstP = taxP / 2;
                    cgstA = taxable * (cgstP / 100);
                    sgstA = taxable * (sgstP / 100);
                } else {
                    // Inter-state: Use IGST
                    igstP = taxP;
                    igstA = taxable * (igstP / 100);
                }
            } else if (showPurchaseTax) {
                // VAT system
                vatP = taxP;
                vatA = taxable * (vatP / 100);
            }

            totalCGST += cgstA;
            totalSGST += sgstA;
            totalIGST += igstA;
            totalVAT += vatA;

            return {
                ...item,
                CGSTPercent: Number(Number(cgstP).toFixed(2)),
                CGSTAmount: Number(Number(cgstA).toFixed(2)),
                SGSTPercent: Number(Number(sgstP).toFixed(2)),
                SGSTAmount: Number(Number(sgstA).toFixed(2)),
                IGSTPercent: Number(Number(igstP).toFixed(2)),
                IGSTAmount: Number(Number(igstA).toFixed(2)),
                VATPercent: Number(Number(vatP).toFixed(2)),
                VATAmount: Number(Number(vatA).toFixed(2)),
                taxAmount: Number((cgstA + sgstA + igstA + vatA).toFixed(2)),
            };
        });

        // Only update items if actually changed (prevents infinite loop)
        setItems(prev => {
            if (JSON.stringify(prev) === JSON.stringify(updated)) return prev;
            return updated;
        });

        const totalTax = totalCGST + totalSGST + totalIGST + totalVAT;
        const grandTotal = subtotal + totalTax;

        setNetTotal(subtotal);
        setTotalPrice(grandTotal);
        setTaxSummary({
            CGST: Number(totalCGST.toFixed(2)),
            SGST: Number(totalSGST.toFixed(2)),
            IGST: Number(totalIGST.toFixed(2)),
            VAT: Number(totalVAT.toFixed(2)),
            totalTax: Number(totalTax.toFixed(2)),
        });

        if (!isTotalPaidModified) {
            setTotalPaid(grandTotal.toFixed(2));
        }
    }, [items, pharmacyStateCode, supplierStateCode, posTaxType, showPurchaseTax, pharmacyPOS, isTotalPaidModified, supplier]);

    // Load suppliers
    useEffect(() => {
        loadSuppliers();
    }, []);

    // Run recalculation when actual data changes
    useEffect(() => {
        recalculateAll();
    }, [items, pharmacyStateCode, supplierStateCode, posTaxType, showPurchaseTax, pharmacyPOS, isTotalPaidModified, supplier]);

    // Validation
    const validateForm = () => {
        let newErrors = {};

        if (items.length === 0) newErrors.items = "At least one item required";
        if (!totalPaid.trim()) newErrors.totalPaid = "Total paid is required";

        // Item-level validation
        const itemErrors = [];

        items.forEach((item, index) => {
            const errors = {};

            // Batch number check
            if (!item.isOTC && (!item.batchNo || item.batchNo.trim() === "")) {
                errors.batchNo = "Required field";
            }

            // Manufacturing date check
            if (!item.manufacturingDate || item.manufacturingDate.trim() === "") {
                errors.manufacturingDate = "Required field";
            } if (item.unitPrice === 0) {
                errors.unitPrice = "Required field"
            } if (item.unitSalePrice === 0) {
                errors.unitSalePrice = "Required field"
            }

            // Expiry date check
            if (!item.expiryDate || item.expiryDate.trim() === "") {
                errors.expiryDate = "Required field";
            }
            // else {
            //     const expiry = new Date(item.expiryDate);
            //     const today = new Date();
            //     today.setHours(0, 0, 0, 0); // remove time part

            //     if (isNaN(expiry.getTime()) || expiry < today) {
            //         errors.expiryDate = "Invalid date";
            //     }
            // }

            itemErrors.push(errors);
        });

        // Check if any item has errors
        const hasItemErrors = itemErrors.some(err => Object.keys(err).length > 0);

        if (hasItemErrors) {
            newErrors.itemErrors = itemErrors;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Submit
    const handleSave = async (e) => {
        e.preventDefault();
        if (!supplier || !supplier._id || supplier._id === "") {
            toast.warning(t("Please select a supplier"));
            return;
        }
        if (!validateForm()) {
            toast.warning(t("Please fill all the required fields"));
            return;
        }

        // Calculate payment status
        const totalAmount = Number(totalPrice);
        const paid = Number(totalPaid) || 0;
        const isPaid = paid >= totalAmount;
        const isPartialPaid = paid > 0 && paid < totalAmount;
        const isSettled = isPaid;

        // Determine tax type for payload
        const useIntraState = determineTaxType(pharmacyStateCode, supplierStateCode);

        const payload = {
            clinicId: pharmacyPOS?.hospital_id,
            pharmacyId: pharmacyPOS?._id,
            supplierName: supplier?.companyName || "",
            supplierId: supplier?._id || "",
            NetTotal: totalPrice.toFixed(2),
            taxableAmount: netTotal.toFixed(2),
            totalPaid: paid.toFixed(2),
            isPaid: isPaid,
            isPartialPaid: isPartialPaid,
            isSettled: isSettled,
            purchaseDate: date,
            notes: note,

            otherNetTotalPrice: 0,
            currencyType: currency,

            // Overall tax info
            taxPercentage: Math.max(...items.map(i => i.taxPercentage), 0),
            tax: taxSummary.totalTax.toFixed(2),
            isIGST: !useIntraState, // true for IGST, false for SGST/CGST
            IGST: taxSummary.IGST.toFixed(2),
            CGST: taxSummary.CGST.toFixed(2),
            SGST: taxSummary.SGST.toFixed(2),
            IGSTPercentage: !useIntraState ? Math.max(...items.map(i => i.taxPercentage), 0) : 0,
            CGSTPercentage: useIntraState ? (Math.max(...items.map(i => i.taxPercentage), 0) / 2) : 0,
            SGSTPercentage: useIntraState ? (Math.max(...items.map(i => i.taxPercentage), 0) / 2) : 0,

            stocks: items.map((item) => {
                const itemSubtotal = Number(item.stkQuantity) * Number(item.unitPrice) - Number(item.discount || 0);

                const totalPackingQty = Number(item.stkQuantity) * Number(item.packageQuantity); // total medicines per batch
                const totalPrice = Number(calculateItemTotalWithTax(item)) || 0;

                const calculatedUnitPrice =
                    totalPackingQty > 0
                        ? (itemSubtotal + Number(item.taxAmount || 0)) / totalPackingQty
                        : 0;

                const calculatedUnitPricePerMedicine =
                    totalPackingQty > 0
                        ? Number(item.unitSalePrice || 0) / Number(item.packageQuantity)
                        : 0;

                return {
                    itemId: item._id,
                    isOTC: item.isOTC,
                    isUnani: item.type === 'unani',
                    code: item.code,
                    brandName: item.brandName,
                    medicineName: item.saltName || "",
                    manufacturerName: item.manufacturer,
                    barcode: item.barcode || "",
                    batchNumber: item.batchNo,
                    manufacturingDate: item.manufacturingDate,
                    expiryDate: item.expiryDate,
                    package: item.package,
                    packing: item.packageQuantity,
                    quantity: item.stkQuantity,
                    taxableAmount: itemSubtotal.toFixed(2),
                    totalPrice,
                    totalMedicine: item.itemQuantity,

                    // NEW UPDATED VALUES
                    unitPrice: calculatedUnitPrice.toFixed(2),
                    unitPricePerMedicine: calculatedUnitPricePerMedicine.toFixed(2),

                    discount: Number(item.discount) || 0,

                    HSN: "123",
                    MFG: "HMD",
                    MRP: Number(item.unitSalePrice).toFixed(2),
                    rate: Number(item.unitPrice).toFixed(2),

                    taxPercentage: item.taxPercentage,
                    tax: item.taxAmount.toFixed(2),
                    isIGST: !useIntraState,
                    IGST: item.IGSTAmount.toFixed(2),
                    CGST: item.CGSTAmount.toFixed(2),
                    SGST: item.SGSTAmount.toFixed(2),
                    IGSTPercentage: !useIntraState ? item.taxPercentage : 0,
                    CGSTPercentage: useIntraState ? item.taxPercentage / 2 : 0,
                    SGSTPercentage: useIntraState ? item.taxPercentage / 2 : 0,
                };
            })
        };
        // console.log('add stock payload:', payload);

        try {
            setLoading(true);
            const res = await addStock(payload);
            toast.success(res.data.message || t("Purchase added successfully"));
            resetForm();
            navigate(-1);
        } catch (err) {
            console.error("Error saving stock:", err);
            // toast.warning(err.response?.data?.message || t("Failed to save purchase"));
        } finally {
            setLoading(false);
        }
    };

    const calculateSubtotal = (item) => {
        const base = item.stkQuantity * item.unitPrice;
        const disc = Number(item.discount) || 0;
        return (base - disc).toFixed(2);
    };

    // calculate subTotal for each item with tax included
    const calculateItemTotalWithTax = (item) => {
        const subtotal = Number(calculateSubtotal(item)) || 0;
        const tax = item.taxAmount || 0;
        return (subtotal + tax).toFixed(2);
    }

    const gotoAddItem = () => {
        navigate(`${process.env.PUBLIC_URL}/add-item`);
    }

    // Update batch number
    const updateBatchNo = (_id, value) => {
        setItems((prev) =>
            prev.map((item) =>
                item._id === _id ? { ...item, batchNo: value } : item
            )
        );
    };

    // Update manufacturing date
    const updateManufacturingDate = (_id, value) => {
        setItems((prev) =>
            prev.map((item) =>
                item._id === _id ? { ...item, manufacturingDate: value } : item
            )
        );
    };

    // Update expiry date
    const updateExpiryDate = (_id, value) => {
        setItems((prev) =>
            prev.map((item) =>
                item._id === _id ? { ...item, expiryDate: value } : item
            )
        );
    };

    const handleSupplierSearchChange = (newValue) => {
        setSearchQuerySupplier(newValue);
    };

    // Reset Form
    const resetForm = () => {
        // setSupplier(null);
        setSearchQuerySupplier("");
        setItems([]);
        setTotalPaid("");
        setNote("");
        setDate(new Date().toISOString().split("T")[0]);
        setErrors({});
        resetResearchSearchState();
        setNetTotal(0);
        setTotalPrice(0);
        setTaxSummary({
            CGST: 0,
            SGST: 0,
            IGST: 0,
            VAT: 0,
            totalTax: 0,
        });
        setIsTotalPaidModified(false);
        setSupplierStateCode("");
        setIsSameState(true);
    };

    const isDark = document.body.classList.contains("dark-only");
    const purchaseSelectTheme = getPurchaseSelectTheme(isDark);
    const purchaseSelectStyles = getPurchaseSelectStyles(isDark);
    const showSearchResults = isResearchActive;

    return (
        <Fragment>
            <Breadcrumbs mainTitle={t("Purchase Items")} parent={t("Dashboard")} title={t("Purchase Items")} />
            <Col sm="12">
                <Card className="purchase-stock-card">
                    <CardBody className="stock-adjustment purchase-stock-body">

                        <Form onSubmit={handleSave} className="purchase-stock-form">
                            <div className="purchase-stock-top">
                                <div className="purchase-stock-toolbar">
                                    <div className="purchase-stock-field purchase-stock-type">
                                        <label className="form-label label-with-info">
                                            {t("Item Type")}
                                            <InfoIcon text={t(PURCHASE_ITEM_SEARCH.itemType)} />
                                        </label>
                                        <div className="purchase-type-switch">
                                            <label className="purchase-type-option">
                                                <input
                                                    type="radio"
                                                    name="purchaseItemType"
                                                    value="medicine"
                                                    checked={researchType === "medicine"}
                                                    onChange={() => handleResearchTypeChange("medicine")}
                                                />
                                                <span>{t("Medicine")}</span>
                                            </label>
                                            <label className="purchase-type-option">
                                                <input
                                                    type="radio"
                                                    name="purchaseItemType"
                                                    value="otc"
                                                    checked={researchType === "otc"}
                                                    onChange={() => handleResearchTypeChange("otc")}
                                                />
                                                <span>OTC</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="purchase-stock-field purchase-stock-search">
                                        <label className="form-label label-with-info">
                                            {t("Search Items")} <span className="text-danger">*</span>
                                            <InfoIcon text={t(PURCHASE_ITEM_SEARCH.selectItemsTooltip)} />
                                        </label>
                                        <div className="input-group purchase-inventory-search">
                                            <AsyncSelect
                                                key={itemSearchKey}
                                                className="flex-grow-1"
                                                classNamePrefix="purchase-select"
                                                cacheOptions
                                                loadOptions={loadMedicines}
                                                defaultOptions={[]}
                                                onChange={handleSelectMedicine}
                                                value={selectedMedicine}
                                                inputValue={researchSearchQuery}
                                                placeholder={t("Search by brand, product, salt, manufacturer, etc.")}
                                                onInputChange={handleMedicineSearchChange}
                                                menuIsOpen={false}
                                                isClearable={true}
                                                isLoading={inventoryLoading}
                                                theme={purchaseSelectTheme}
                                                styles={purchaseSelectStyles}
                                            />
                                            <div className="input-group-append" style={{ cursor: "pointer" }}>
                                                <span
                                                    className="input-group-text cursor-pointer h-100"
                                                    id="AddItemToolTip"
                                                    onClick={gotoAddItem}
                                                >
                                                    <PlusCircle size={16} />
                                                </span>
                                                <ToolTip
                                                    attrToolTip={{
                                                        placement: "top",
                                                        isOpen: addItemTooltipOpen,
                                                        target: "AddItemToolTip",
                                                        toggle: toggleAddItemTooltip,
                                                    }}
                                                >
                                                    <strong>{t("Add An Item")}</strong>
                                                </ToolTip>
                                            </div>
                                        </div>
                                        {errors.items && <div className="text-danger mt-1 small">{errors.items}</div>}
                                    </div>

                                    <div className="purchase-stock-field purchase-stock-supplier">
                                        <label className="form-label">
                                            {t("Supplier")} <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <AsyncSelect
                                                className="flex-grow-1"
                                                classNamePrefix="purchase-select"
                                                cacheOptions
                                                loadOptions={loadSuppliers}
                                                defaultOptions={recentSuppliers}
                                                value={supplier ? {
                                                    value: supplier._id,
                                                    label: `${supplier.companyName} [${supplier.contactPerson || "No Contact"}]`,
                                                    raw: supplier
                                                } : null}
                                                onChange={handleSelectSupplier}
                                                placeholder={t("Search & select a supplier")}
                                                onInputChange={handleSupplierSearchChange}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                menuPlacement="auto"
                                                noOptionsMessage={() =>
                                                    searchQuerySupplier
                                                        ? t("We couldn't find any supplier matching your search. Please try a different name.")
                                                        : t("Start typing to search...")
                                                }
                                                isClearable={true}
                                                isLoading={loading}
                                                theme={purchaseSelectTheme}
                                                styles={purchaseSelectStyles}
                                            />
                                            <div className="input-group-append" style={{ cursor: "pointer" }}>
                                                <span
                                                    className="input-group-text cursor-pointer h-100"
                                                    id="AddSupplierToolTip"
                                                    onClick={toggleAddSupplierModal}
                                                >
                                                    <PlusCircle size={16} />
                                                </span>
                                                <ToolTip
                                                    attrToolTip={{
                                                        placement: "top",
                                                        isOpen: addSupplierTooltipOpen,
                                                        target: "AddSupplierToolTip",
                                                        toggle: toggleAddSupplierTooltip,
                                                    }}
                                                >
                                                    <strong>{t("Add A New Supplier")}</strong>
                                                </ToolTip>
                                            </div>
                                        </div>
                                        {errors.supplier && <div className="text-danger small">{errors.supplier}</div>}
                                    </div>

                                    <div className="purchase-stock-back">
                                        <button
                                            type="button"
                                            className="btn btn-primary purchase-stock-back-btn"
                                            onClick={() => navigate(-1)}
                                        >
                                            <FaArrowLeft className="me-1" /> {t("Back")}
                                        </button>
                                    </div>
                                </div>

                                {showSearchResults && (
                                    <div className="purchase-dual-results-wrap">
                                        <div className="row purchase-dual-results g-2">
                                            <div className="col-lg-6">
                                                <div className="purchase-research-section purchase-results-panel purchase-results-panel--inventory">
                                                    <div className="purchase-research-header">
                                                        <span className="purchase-research-title label-with-info">
                                                            {t("Pharmacy Inventory")}
                                                            <InfoIcon text={t(PURCHASE_ITEM_SEARCH.selectItems)} />
                                                        </span>
                                                    </div>
                                                    <div className="purchase-research-body">
                                                        {inventoryLoading ? (
                                                            <div className="purchase-research-state">
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                                                                {t("Searching pharmacy inventory...")}
                                                            </div>
                                                        ) : inventoryResults.length === 0 ? (
                                                            <div className="purchase-research-state">
                                                                {t("No matching items in your pharmacy inventory.")}
                                                            </div>
                                                        ) : (
                                                            <div className="purchase-results-list">
                                                                {inventoryResults.map((med) => (
                                                                    <PurchaseSearchResultRow
                                                                        key={med._id}
                                                                        t={t}
                                                                        productForm={med.productForm}
                                                                        name={med.brandName || med.saltName || "—"}
                                                                        packageLabel={med.package}
                                                                        manufacturer={med.manufacturer}
                                                                        quantity={med.packageQuantity ?? med.quantity ?? null}
                                                                        composition={getInventoryComposition(med)}
                                                                        typeLabel={getInventoryTypeLabel(med, t)}
                                                                        typeClass={getInventoryTypeBadgeClass(med)}
                                                                        onAdd={() => handleSelectInventoryItem(med)}
                                                                        selectVariant
                                                                    />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-lg-6">
                                                <div className="purchase-research-section purchase-results-panel purchase-results-panel--master">
                                                    <div className="purchase-research-header">
                                                        <span className="purchase-research-title label-with-info">
                                                            {t("Master Database")}
                                                            <InfoIcon text={t(PURCHASE_ITEM_SEARCH.databaseSection)} />
                                                        </span>
                                                    </div>
                                                    <div className="purchase-research-body">
                                                        {researchLoading && researchMedicines.length === 0 ? (
                                                            <div className="purchase-research-state">
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                                                                {t(PURCHASE_ITEM_SEARCH.databaseLoading)}
                                                            </div>
                                                        ) : researchMedicines.length === 0 ? (
                                                            <div className="purchase-research-state">
                                                                {t(PURCHASE_ITEM_SEARCH.databaseEmpty)}
                                                            </div>
                                                        ) : (
                                                            <div className="purchase-results-list">
                                                                {researchMedicines.map((m) => (
                                                                    <PurchaseSearchResultRow
                                                                        key={m._id}
                                                                        t={t}
                                                                        productForm={m.product_form}
                                                                        name={m.name || "—"}
                                                                        packageLabel={m.package}
                                                                        manufacturer={m.manufacturer}
                                                                        quantity={getMasterMedicineQuantity(m)}
                                                                        composition={getMasterComposition(m, isResearchOTC)}
                                                                        typeLabel={getMasterTypeLabel(m, isResearchOTC, t)}
                                                                        typeClass={getMasterTypeBadgeClass(m, isResearchOTC)}
                                                                        onAdd={() => handleQuickAddFromResearch(m)}
                                                                        adding={addingResearchId === m._id}
                                                                    />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="purchase-stock-scroll">
                                <div className="row">
                                    <div className="col-md-12">
                                        {items.length > 0 && (
                                            <div className="mt-3 mb-3 table-responsive table-wrap stock-adjustment">
                                                <Table className="align-middle rounded shadow-sm table-bordered" bsSize="sm">
                                                    <thead className="table-light">
                                                        <tr>
                                                            {/* <th rowSpan="2" className="fw-bold text-nowrap">{t("Product")}
                                                            <InfoIcon text={COLUMN_INFO.product} />
                                                        </th> */}
                                                            <th rowSpan="2" className="fw-bold text-nowrap">{t("Brand")}
                                                                <InfoIcon text={COLUMN_INFO.brand} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">{t("Batch")}
                                                                <InfoIcon text={COLUMN_INFO.batch} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">{t("Mfg Date")}
                                                                <InfoIcon text={COLUMN_INFO.mfgDate} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">{t("Exp Date")}
                                                                <InfoIcon text={COLUMN_INFO.expDate} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">{t("Package")}
                                                                <InfoIcon text={COLUMN_INFO.package} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">{t("Pkg Qty")}
                                                                <InfoIcon text={COLUMN_INFO.pkgQty} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold">{t("Total Unit Qty")}
                                                                <InfoIcon text={COLUMN_INFO.totalUnitQty} />
                                                            </th>
                                                            {/* <th rowSpan="2" className="fw-bold text-wrap">{t("Regular Price")}
                                                            <InfoIcon text={COLUMN_INFO.regularPrice} />
                                                        </th> */}
                                                            <th rowSpan="2" className="fw-bold text-nowrap">{t("PKG Cost")}
                                                                <InfoIcon text={COLUMN_INFO.pkgRate} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">{t("Discount")}
                                                                <InfoIcon text={COLUMN_INFO.discount} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold">{t("Gross value")}
                                                                <InfoIcon text={COLUMN_INFO.grossValue} />
                                                            </th>

                                                            {/* TAX GROUP */}
                                                            {showPurchaseTax && (
                                                                <th colSpan="2" className="text-center fw-bold bg-light-primary text-dark">{t("Tax")}
                                                                    <InfoIcon text={COLUMN_INFO.tax} />
                                                                </th>
                                                            )}

                                                            {/* GST Groups */}
                                                            {/* {posTaxType === "gst" ? (
                                                            isSameState ? (
                                                                <>
                                                                    <th colSpan="2" className="text-center fw-bold bg-light-primary text-dark">{t("CGST")}</th>
                                                                    <th colSpan="2" className="text-center fw-bold bg-light-primary text-dark">{t("SGST")}</th>
                                                                </>
                                                            ) : (
                                                                <th colSpan="2" className="text-center fw-bold bg-light-primary text-dark">{t("IGST")}</th>
                                                            )
                                                        ) : (
                                                            <th colSpan="2" className="text-center fw-bold bg-light-primary text-dark">{t("VAT")}</th>
                                                        )} */}
                                                            <th rowSpan="2" className="fw-bold text-nowrap">{t("Subtotal")}
                                                                <InfoIcon text={COLUMN_INFO.subtotal} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold text-nowrap">{t("PKG MRP")}
                                                                <InfoIcon text={COLUMN_INFO.pkgMrp} />
                                                            </th>
                                                            <th rowSpan="2" className="fw-bold">

                                                            </th>
                                                        </tr>

                                                        {/* SECOND ROW — Sub-headings */}
                                                        <tr>
                                                            {/* TAX */}
                                                            {showPurchaseTax && (
                                                                <>
                                                                    <th className="bg-light-primary text-dark text-nowrap">
                                                                        {posTaxType === "gst" ? t("GST") : t("VAT")} %</th>
                                                                    <th className="bg-light-primary text-dark">{t("Amt")}</th>
                                                                </>
                                                            )}

                                                            {/* GST */}
                                                            {/* {posTaxType === "gst" ? (
                                                            isSameState ? (
                                                                <>
                                                                    <th className="bg-light-primary text-dark">%</th><th className="bg-light-primary text-dark">{t("Amt")}</th>
                                                                    <th className="bg-light-primary text-dark">%</th><th className="bg-light-primary text-dark">{t("Amt")}</th>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <th className="bg-light-primary text-dark">%</th><th className="bg-light-primary text-dark">{t("Amt")}</th>
                                                                </>
                                                            )
                                                        ) : (
                                                            <>
                                                                <th className="bg-light-primary text-dark">%</th><th className="bg-light-primary text-dark">{t("Amt")}</th>
                                                            </>
                                                        )} */}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {items.map((item, index) => (
                                                            <tr key={item._id}>
                                                                {/* <td className="border-b-2" style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={<Tooltip id={`tooltip-${item._id}`}>{item.brandName}</Tooltip>}
                                                                >
                                                                    <span>{item.brandName}</span>
                                                                </OverlayTrigger>
                                                            </td> */}
                                                                <td className="border-b-2" style={{ maxWidth: "130px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={<Tooltip id={`tooltip-${item._id}`}>{item.brandName}</Tooltip>}
                                                                    >
                                                                        <span>{item.brandName}</span>
                                                                    </OverlayTrigger>
                                                                </td>
                                                                <td>
                                                                    {/* {item.isOTC === false || item.isOTC === null ? ( */}
                                                                    <>
                                                                        <input
                                                                            type="text"
                                                                            value={item.batchNo || ""}
                                                                            className="form-control form-control-sm"
                                                                            placeholder={t("batch")}
                                                                            onChange={(e) => updateBatchNo(item._id, e.target.value)}
                                                                        />
                                                                        {errors.itemErrors?.[index]?.batchNo && (
                                                                            <small className="text-danger">{errors.itemErrors[index].batchNo}</small>
                                                                        )}
                                                                    </>
                                                                    {/* ) : (
                                                                    <>-</>
                                                                )} */}
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="date"
                                                                        value={item.manufacturingDate || ""}
                                                                        className="form-control form-control-sm"
                                                                        // max={new Date().toISOString().split("T")[0]} // 🔒 Block future dates
                                                                        onChange={(e) => updateManufacturingDate(item._id, e.target.value)}
                                                                    />
                                                                    {errors.itemErrors?.[index]?.manufacturingDate && (
                                                                        <small className="text-danger">{errors.itemErrors[index].manufacturingDate}</small>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="date"
                                                                        value={item.expiryDate || ""}
                                                                        className="form-control form-control-sm"
                                                                        // min={item.manufacturingDate || new Date().toISOString().split("T")[0]} // 🔒 Block dates before manufacturing date
                                                                        onChange={(e) => updateExpiryDate(item._id, e.target.value)}
                                                                    />
                                                                    {errors.itemErrors?.[index]?.expiryDate && (
                                                                        <small className="text-danger">{errors.itemErrors[index].expiryDate}</small>
                                                                    )}
                                                                </td>
                                                                <td className="text-nowrap">{item.package} ({item.packageQuantity})</td>
                                                                {/* Package Quantity */}
                                                                <td>
                                                                    <div className="input-group input-group-sm custom-qty-input table-price-counter-nowrap">
                                                                        <button
                                                                            type="button"
                                                                            className="button-minus icon-shape icon-sm btn-danger"
                                                                            onClick={() =>
                                                                                updatePackageQty(
                                                                                    item._id,
                                                                                    item.stkQuantity - 1
                                                                                )
                                                                            }
                                                                        >
                                                                            -
                                                                        </button>
                                                                        <input
                                                                            type="number"
                                                                            value={item.stkQuantity}
                                                                            className="border-0 quantity-field incrementor"
                                                                            onChange={(e) =>
                                                                                updatePackageQty(
                                                                                    item._id,
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            {...disableScrollProps}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="button-plus icon-shape icon-sm btn-primary"
                                                                            onClick={() =>
                                                                                updatePackageQty(
                                                                                    item._id,
                                                                                    item.stkQuantity + 1
                                                                                )
                                                                            }
                                                                        >
                                                                            +
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                                <td className="text-center">{item.itemQuantity}</td>
                                                                {/* <td >{getCurrencySymbol()}{item.regularPrice}</td> */}
                                                                {/* Unit Price (rate)*/}
                                                                <td>
                                                                    <div className="input-group input-group-sm custom-qty-input table-price-counter-nowrap">
                                                                        {/* <button
                                                                        type="button"
                                                                        className="button-minus icon-shape icon-sm btn-danger"
                                                                        onClick={() => updateItem(item._id, "unitPrice", Math.max(0, item.unitPrice - 1))}
                                                                    >
                                                                        -
                                                                    </button> */}
                                                                        <input
                                                                            type="text"
                                                                            inputMode="decimal"
                                                                            className="form-control form-control-sm"
                                                                            value={item.unitPrice ?? ""}
                                                                            onKeyDown={(e) => {
                                                                                // Block invalid keys
                                                                                if (["-", "+", "e", "E"].includes(e.key)) {
                                                                                    e.preventDefault();
                                                                                }

                                                                                // Only one dot
                                                                                if (e.key === "." && String(item.unitPrice).includes(".")) {
                                                                                    e.preventDefault();
                                                                                }
                                                                            }}
                                                                            onChange={(e) => {
                                                                                let value = e.target.value;

                                                                                // Allow only digits and dot
                                                                                value = value.replace(/[^0-9.]/g, "");

                                                                                // Keep only ONE dot
                                                                                const dotCount = value.split(".").length - 1;
                                                                                if (dotCount > 1) return;

                                                                                // Allow typing "12." or "."
                                                                                updateItem(item._id, "unitPrice", value);
                                                                            }}
                                                                            onBlur={() => {
                                                                                // Normalize on blur
                                                                                const num = parseFloat(item.unitPrice);
                                                                                updateItem(
                                                                                    item._id,
                                                                                    "unitPrice",
                                                                                    isNaN(num) ? "0" : num.toFixed(2)
                                                                                );
                                                                            }}
                                                                            onPaste={(e) => {
                                                                                const pasted = e.clipboardData.getData("text");
                                                                                if (!/^\d*\.?\d*$/.test(pasted)) {
                                                                                    e.preventDefault();
                                                                                }
                                                                            }}
                                                                        />

                                                                        {/* <button
                                                                        type="button"
                                                                        className="button-plus icon-shape icon-sm btn-primary"
                                                                        onClick={() => updateItem(item._id, "unitPrice", item.unitPrice + 1)}
                                                                    >
                                                                        +
                                                                    </button> */}
                                                                    </div>
                                                                    {errors.itemErrors?.[index]?.unitPrice && (
                                                                        <small className="text-danger">{errors.itemErrors[index].unitPrice}</small>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <Input type="text" bsSize="sm" value={item.discount || ""}
                                                                        onKeyDown={(e) => {
                                                                            // Block invalid keys
                                                                            if (["-", "+", "e", "E"].includes(e.key)) {
                                                                                e.preventDefault();
                                                                            }

                                                                            // Only one dot
                                                                            if (e.key === "." && String(item.discount).includes(".")) {
                                                                                e.preventDefault();
                                                                            }
                                                                        }}
                                                                        onChange={(e) => {
                                                                            let value = e.target.value;

                                                                            // Allow only digits and dot
                                                                            value = value.replace(/[^0-9.]/g, "");

                                                                            // Keep only ONE dot
                                                                            const dotCount = value.split(".").length - 1;
                                                                            if (dotCount > 1) return;

                                                                            // Allow typing "12." or "."
                                                                            updateItem(item._id, "discount", value);
                                                                        }}
                                                                        onBlur={() => {
                                                                            // Normalize on blur
                                                                            const num = parseFloat(item.discount);
                                                                            updateItem(
                                                                                item._id,
                                                                                "discount",
                                                                                isNaN(num) ? "0" : num.toFixed(2)
                                                                            );
                                                                        }}
                                                                        onPaste={(e) => {
                                                                            const pasted = e.clipboardData.getData("text");
                                                                            if (!/^\d*\.?\d*$/.test(pasted)) {
                                                                                e.preventDefault();
                                                                            }
                                                                        }}
                                                                    />
                                                                </td>
                                                                <td className="text-success fw-bold">{getCurrencySymbol()}{calculateSubtotal(item)}</td>
                                                                {showPurchaseTax && (
                                                                    <>
                                                                        <td>
                                                                            {shouldApplyTaxToStockItem(pharmacyPOS, item) ? (
                                                                                <Input type="text" bsSize="sm" value={item.taxPercentage}
                                                                                    onKeyDown={(e) => {
                                                                                        if (["-", "+", "e", "E"].includes(e.key)) {
                                                                                            e.preventDefault();
                                                                                        }
                                                                                        if (e.key === "." && String(item.taxPercentage).includes(".")) {
                                                                                            e.preventDefault();
                                                                                        }
                                                                                    }}
                                                                                    onChange={(e) => {
                                                                                        let value = e.target.value;
                                                                                        value = value.replace(/[^0-9.]/g, "");
                                                                                        const dotCount = value.split(".").length - 1;
                                                                                        if (dotCount > 1) return;
                                                                                        updateItem(item._id, "taxPercentage", value);
                                                                                    }}
                                                                                    onBlur={() => {
                                                                                        const num = parseFloat(item.taxPercentage);
                                                                                        updateItem(
                                                                                            item._id,
                                                                                            "taxPercentage",
                                                                                            isNaN(num) ? "0" : num.toFixed(2)
                                                                                        );
                                                                                    }}
                                                                                    onPaste={(e) => {
                                                                                        const pasted = e.clipboardData.getData("text");
                                                                                        if (!/^\d*\.?\d*$/.test(pasted)) {
                                                                                            e.preventDefault();
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <span className="text-muted">-</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="text-success">
                                                                            {shouldApplyTaxToStockItem(pharmacyPOS, item)
                                                                                ? `${getCurrencySymbol()}${item.taxAmount?.toFixed(2)}`
                                                                                : "-"}
                                                                        </td>
                                                                    </>
                                                                )}

                                                                {/* GST/VAT */}
                                                                {/* {posTaxType === "gst" ? (
                                                                isSameState ? (
                                                                    <>
                                                                        <td className="text-muted">{(item.taxPercentage / 2).toFixed(2)}</td>
                                                                        <td className="text-success">{getCurrencySymbol()}{item.CGSTAmount.toFixed(2)}</td>

                                                                        <td className="text-muted">{(item.taxPercentage / 2).toFixed(2)}</td>
                                                                        <td className="text-success">{getCurrencySymbol()}{item.SGSTAmount.toFixed(2)}</td>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <td className="text-muted">{item.taxPercentage.toFixed(2)}</td>
                                                                        <td className="text-success">{getCurrencySymbol()}{item.IGSTAmount.toFixed(2)}</td>
                                                                    </>
                                                                )
                                                            ) : (
                                                                <>
                                                                    <td className="text-muted">{item.taxPercentage.toFixed(2)}</td>
                                                                    <td className="text-success">{getCurrencySymbol()}{item.VATAmount.toFixed(2)}</td>
                                                                </>
                                                            )} */}
                                                                {/* subTotal */}
                                                                <td className="text-success fw-bold">
                                                                    {getCurrencySymbol()}{calculateItemTotalWithTax(item)}
                                                                    {/* Display Net Unit Cost = (Total Price / Total Units) */}
                                                                    <div className="mt-1 small text-muted" style={{ fontSize: '9px' }}>
                                                                        {t("Unit Cost")}: {getCurrencySymbol()}{
                                                                            item.itemQuantity > 0
                                                                                ? (Number(calculateItemTotalWithTax(item)) / Number(item.itemQuantity)).toFixed(2)
                                                                                : "0.00"
                                                                        }
                                                                    </div>
                                                                </td>
                                                                {/* Sale Price */}
                                                                <td>
                                                                    <div className="input-group input-group-sm custom-qty-input table-price-counter-nowrap">
                                                                        {/* <button
                                                                        type="button"
                                                                        className="button-minus icon-shape icon-sm btn-danger"
                                                                        onClick={() => updateItem(item._id, "unitSalePrice", Math.max(0, item.unitSalePrice - 1))}
                                                                    >
                                                                        -
                                                                    </button> */}
                                                                        <input
                                                                            type="text"
                                                                            inputMode="decimal"
                                                                            pattern="[0-9]*\.?[0-9]*"
                                                                            value={item.unitSalePrice}
                                                                            className="form-control form-control-sm"
                                                                            onChange={(e) => {
                                                                                let value = e.target.value;

                                                                                // Allow only digits and dot
                                                                                value = value.replace(/[^0-9.]/g, "");

                                                                                // Keep only ONE dot
                                                                                const dotCount = value.split(".").length - 1;
                                                                                if (dotCount > 1) return;

                                                                                // Allow typing "12." or "."
                                                                                updateItem(item._id, "unitSalePrice", value);
                                                                            }}
                                                                            onBlur={() => {
                                                                                // Normalize on blur
                                                                                const num = parseFloat(item.unitSalePrice);
                                                                                updateItem(
                                                                                    item._id,
                                                                                    "unitSalePrice",
                                                                                    isNaN(num) ? "0" : num.toFixed(2)
                                                                                );
                                                                            }}
                                                                            onPaste={(e) => {
                                                                                const pasted = e.clipboardData.getData("text");
                                                                                if (!/^\d*\.?\d*$/.test(pasted)) {
                                                                                    e.preventDefault();
                                                                                }
                                                                            }}
                                                                            {...disableScrollProps}
                                                                        />
                                                                        {/* <button
                                                                        type="button"
                                                                        className="button-plus icon-shape icon-sm btn-primary"
                                                                        onClick={() => updateItem(item._id, "unitSalePrice", item.unitSalePrice + 1)}
                                                                    >
                                                                        +
                                                                    </button> */}
                                                                    </div>
                                                                    {errors.itemErrors?.[index]?.unitSalePrice && (
                                                                        <small className="text-danger">{errors.itemErrors[index].unitSalePrice}</small>
                                                                    )}
                                                                    {/* Display Unit MRP = (PKG MRP / Package Size) */}
                                                                    <div className="mt-1 small text-muted" style={{ fontSize: '9px' }}>
                                                                        {t("Unit MRP")}: {getCurrencySymbol()}{
                                                                            item.packageQuantity > 0
                                                                                ? (Number(item.unitSalePrice || 0) / Number(item.packageQuantity)).toFixed(2)
                                                                                : "0.00"
                                                                        }
                                                                    </div>
                                                                </td>
                                                                {/* <td className="text-center"> */}
                                                                <button
                                                                    type="button"
                                                                    className="btn-danger btn-sm remove-item"
                                                                    onClick={() => removeItem(item._id)}
                                                                >
                                                                    &times;
                                                                </button>
                                                                {/* </td> */}
                                                            </tr>
                                                        ))}

                                                        {/*Sub Totals */}
                                                        <tr>
                                                            <td colSpan="5">
                                                                <strong>{t("Sub Totals")}:</strong>
                                                            </td>
                                                            <td className="text-center">
                                                                {items.reduce((acc, item) => acc + Number(item.stkQuantity), 0)}
                                                            </td>
                                                            {/* Total Qty */}
                                                            <td className="text-center">
                                                                {items.reduce((acc, item) => acc + Number(item.itemQuantity), 0)}
                                                            </td>
                                                            {/* total rate */}
                                                            <td>
                                                                {/* Empty cell for rate */}
                                                            </td>
                                                            {/* discount total */}
                                                            <td className="text-success fw-bold">
                                                                {getCurrencySymbol()}{items.reduce((acc, item) => acc + Number(item.discount || 0), 0).toFixed(2)}
                                                            </td>
                                                            {/* gross value total */}
                                                            <td className="text-success fw-bold">
                                                                {getCurrencySymbol()}{netTotal.toFixed(2)}
                                                            </td>

                                                            {/* tax amount total */}
                                                            {showPurchaseTax && (
                                                                <>
                                                                    <td></td>
                                                                    <td className="text-success fw-bold">
                                                                        {getCurrencySymbol()}{items.reduce((acc, item) => acc + Number(item.taxAmount || 0), 0).toFixed(2)}
                                                                    </td>
                                                                </>
                                                            )}
                                                            {/* GST/VAT totals */}
                                                            {/* {posTaxType === "gst" ? (
                                                            isSameState ? (
                                                                <>
                                                                    <td></td>
                                                                    <td className="text-success fw-bold">
                                                                        {getCurrencySymbol()}{items.reduce((acc, item) => acc + Number(item.CGSTAmount || 0), 0).toFixed(2)}
                                                                    </td>
                                                                    <td></td>
                                                                    <td className="text-success fw-bold">
                                                                        {getCurrencySymbol()}{items.reduce((acc, item) => acc + Number(item.SGSTAmount || 0), 0).toFixed(2)}
                                                                    </td>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <td></td>
                                                                    <td className="text-success fw-bold">
                                                                        {getCurrencySymbol()}{items.reduce((acc, item) => acc + Number(item.IGSTAmount || 0), 0).toFixed(2)}
                                                                    </td>
                                                                </>
                                                            )
                                                        ) : (
                                                            <>
                                                                <td></td>
                                                                <td className="text-success fw-bold">
                                                                    {getCurrencySymbol()}{items.reduce((acc, item) => acc + Number(item.VATAmount || 0), 0).toFixed(2)}
                                                                </td>
                                                            </>
                                                        )} */}
                                                            {/* sub total */}
                                                            <td className="text-success fw-bold">
                                                                {getCurrencySymbol()}{totalPrice.toFixed(2)}
                                                            </td>
                                                            <td></td>
                                                            <td></td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </div>
                                    {/* Bottom Summary - GST Wise Breakdown */}
                                    {items.length > 0 && (
                                        <div className="row mt-5">
                                            {showPurchaseTax && (
                                            <div className="col-md-8">
                                                <Table bordered bsSize="sm" className="mb-4">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>{t("GST %")}</th>
                                                            <th className="text-end">{t("Taxable Value")}</th>
                                                            {posTaxType === "gst" && isSameState ? (
                                                                <>
                                                                    <th className="text-end">{t("CGST Amt")}</th>
                                                                    <th className="text-end">{t("SGST Amt")}</th>
                                                                </>
                                                            ) : (
                                                                <th className="text-end">{t("IGST Amt")}</th>
                                                            )}
                                                            <th className="text-end">{t("Tax Amt")}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {/* Group items by tax percentage */}
                                                        {Object.entries(
                                                            items.reduce((acc, item) => {
                                                                const rate = item.taxPercentage;
                                                                if (!acc[rate]) {
                                                                    acc[rate] = {
                                                                        taxable: 0,
                                                                        cgst: 0,
                                                                        sgst: 0,
                                                                        igst: 0,
                                                                        totalTax: 0,
                                                                    };
                                                                }
                                                                const taxable = Number(item.stkQuantity) * Number(item.unitPrice) - Number(item.discount || 0);
                                                                acc[rate].taxable += taxable;
                                                                acc[rate].cgst += item.CGSTAmount;
                                                                acc[rate].sgst += item.SGSTAmount;
                                                                acc[rate].igst += item.IGSTAmount;
                                                                acc[rate].totalTax += item.taxAmount;
                                                                return acc;
                                                            }, {})
                                                        )
                                                            .sort(([a], [b]) => a - b)
                                                            .map(([rate, data]) => (
                                                                <>
                                                                    <tr key={rate}>
                                                                        <td className="text-center fw-bold">{rate}%</td>
                                                                        <td className="text-end">{getCurrencySymbol()}{data.taxable.toFixed(2)}</td>
                                                                        {posTaxType === "gst" && isSameState ? (
                                                                            <>
                                                                                <td className="text-end text-success">{getCurrencySymbol()}{data.cgst.toFixed(2)}</td>
                                                                                <td className="text-end text-success">{getCurrencySymbol()}{data.sgst.toFixed(2)}</td>
                                                                            </>
                                                                        ) : (
                                                                            <td className="text-end text-success">{getCurrencySymbol()}{data.igst.toFixed(2)}</td>
                                                                        )}
                                                                        <td className="text-end text-success">{getCurrencySymbol()}{data.totalTax.toFixed(2)}</td>
                                                                    </tr>
                                                                </>
                                                            ))}
                                                        {/* // total row */}
                                                        <tr>
                                                            <td colSpan="2">
                                                            </td>
                                                            {posTaxType === "gst" && isSameState && (
                                                                <>
                                                                    <td className="text-end">
                                                                        {getCurrencySymbol()}{taxSummary.CGST.toFixed(2)}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        {getCurrencySymbol()}{taxSummary.SGST.toFixed(2)}
                                                                    </td>
                                                                </>
                                                            )}
                                                            {posTaxType === "gst" && !isSameState && (
                                                                <td className="text-end">
                                                                    {getCurrencySymbol()}{taxSummary.IGST.toFixed(2)}
                                                                </td>
                                                            )}
                                                            {posTaxType !== "gst" && (
                                                                <td className="text-end text-success w-25">{getCurrencySymbol()}{taxSummary.VAT.toFixed(2)}</td>
                                                            )}
                                                            <td className="text-end">
                                                                {getCurrencySymbol()}{taxSummary.totalTax.toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </div>
                                            )}
                                            {/* Final Tax Addition & Grand Total */}
                                            <div className={showPurchaseTax ? "col-md-4" : "col-md-12"}>
                                                <div className="row align-items-center justify-content-between">
                                                    <div className="col-8">
                                                        <p>
                                                            {t("Net Taxable Value")}:
                                                        </p>
                                                    </div>
                                                    <div className="col-4 text-end">
                                                        <span className="fw-bold">{getCurrencySymbol()}{netTotal.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                                <Table bordered className="table-sm">
                                                    <tbody>
                                                        {showPurchaseTax && posTaxType === "gst" && isSameState && (
                                                            <>
                                                                <tr>
                                                                    <td className="text-end">Add: CGST</td>
                                                                    <td className="text-end text-success w-25">{getCurrencySymbol()}{taxSummary.CGST.toFixed(2)}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="text-end">Add: SGST</td>
                                                                    <td className="text-end text-success w-25">{getCurrencySymbol()}{taxSummary.SGST.toFixed(2)}</td>
                                                                </tr>
                                                            </>
                                                        )}
                                                        {showPurchaseTax && posTaxType === "gst" && !isSameState && (
                                                            <tr>
                                                                <td className="text-end">Add: IGST</td>
                                                                <td className="text-end text-success w-25">{getCurrencySymbol()}{taxSummary.IGST.toFixed(2)}</td>
                                                            </tr>
                                                        )}
                                                        {showPurchaseTax && posTaxType !== "gst" && (
                                                            <tr>
                                                                <td className="text-end">Add: VAT</td>
                                                                <td className="text-end text-success w-25">{getCurrencySymbol()}{taxSummary.VAT.toFixed(2)}</td>
                                                            </tr>
                                                        )}
                                                        <tr className="table-primary">
                                                            <td className="text-end fw-bold">{t("Grand Amount")}</td>
                                                            <td className="text-end fw-bold fs-5 text-primary">{getCurrencySymbol()}{totalPrice.toFixed(2)}</td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </div>

                                            {/* Total Paid */}
                                            <div className="row justify-content-end mt-4">
                                                <div className="col-6">
                                                    <div className="row g-3">
                                                        <div className="col-6">
                                                            <Form.Group>
                                                                <Form.Label className="fw-bold">{t("Total Paid")} *</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    value={totalPaid}
                                                                    onChange={(e) => {
                                                                        setTotalPaid(e.target.value);
                                                                        setIsTotalPaidModified(true);
                                                                    }}
                                                                    className="text-end fw-bold"
                                                                    style={{ fontSize: "1.1rem" }}
                                                                />
                                                            </Form.Group>
                                                        </div>
                                                        <div className="col-6">
                                                            <Form.Group>
                                                                <Form.Label>{t("Purchase Date")}</Form.Label>
                                                                {/* disable future dates */}
                                                                <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} max={new Date().toISOString().split("T")[0]} />
                                                            </Form.Group>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Note */}
                                            <div className="mt-4">
                                                <Form.Group>
                                                    <Form.Label>{t("Note")}</Form.Label>
                                                    <Form.Control as="textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any additional notes..." />
                                                </Form.Group>
                                            </div>
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-end gap-3 mt-4">
                                        <Button variant="primary" type="submit" disabled={loading || items.length === 0}>
                                            {loading ? "Saving..." : t("Save Purchase")}
                                        </Button>
                                        <Button variant="secondary" onClick={() => navigate(-1)}>{t("Cancel")}</Button>
                                    </div>
                                </div>
                            </div>

                        </Form>
                    </CardBody>
                </Card>
            </Col>

            <SupplierModal
                show={showAddSupplierModal}
                onHide={() => setShowAddSupplierModal(false)}
                clinicId={pharmacyPOS?.hospital_id}
                onSaved={(newSupp) => {
                    const s = newSupp.supplier || newSupp;
                    setSupplier(s);
                    setSupplierStateCode(s.stateCode || "");

                    // Update tax type after adding new supplier
                    const newIsSameState = determineTaxType(pharmacyStateCode, s.stateCode);
                    setIsSameState(newIsSameState);
                }}
            />

            <ResearchItemDetailsModal
                isOpen={researchDetailsOpen}
                onClose={handleResearchDetailsClose}
                medicine={pendingResearchMedicine}
                isOTC={isResearchOTC}
                onConfirm={handleResearchDetailsConfirm}
                submitting={!!addingResearchId}
            />
        </Fragment >
    );
};

export default AddStock;